import {
  CloseCircleFilled,
  CopyFilled,
  DeleteOutlined,
  EditOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { message, Modal, Popconfirm, Tabs, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { issueApi } from "api/issue.api";
import { issueCategoryApi } from "api/issueCategory.api";
import { AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  TableContent,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { Issue, IssueStatus, IssueStatusTrans } from "types/issue";
import { IssueCategory } from "types/issueCategory";
import { QueryParam } from "types/query";
import { formatDate } from "utils/date";
import type { Dayjs } from "utils/dayjs";
import { IssueModal } from "views/Issue/components/IssueModal";

interface IssueQuery extends QueryParam {
  issueCategoryId?: number | null;
  status?: string;
  fromAt?: number | null;
  toAt?: number | null;
}

type CountIssue = {
  pending: number;
  processing: number;
  completed: number;
  rejected: number;
};

export const IssuePage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<IssueQuery>({
    page: 1,
    limit: 10,
    search: "",
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Issue[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Partial<Issue>>({});
  const [countIssue, setCountIssue] = useState<CountIssue>({
    pending: 0,
    processing: 0,
    completed: 0,
    rejected: 0,
  } as CountIssue);

  const [issueCategoryId, setIssueCategoryId] = useState<number>();
  const [issueCategoryOptions, setIssueCategoryOptions] = useState<IssueCategory[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [openQRModal, setOpenQRModal] = useState(false);

  const debounceSearch = useCallback(
    debounce((keyword: string) => setQuery({ ...query, search: keyword, page: 1, limit: 20 }), 300),
    [setQuery, query],
  );

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await issueApi.findAll(query);
    setLoading(false);
    setData(res.data.issues);
    setTotal(res.data.total);
  };

  const handleDelete = async (issue: Issue) => {
    setLoading(true);
    try {
      await issueApi.delete(issue.id);
      fetchData();
      fetchCountIssue();
      message.success("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo<TableColumnsType<Issue>>(
    () => [
      {
        title: "STT",
        key: "stt",
        width: 80,
        render: (_, __, index) => <span>{(query.page - 1) * query.limit + index + 1}</span>,
      },
      {
        title: "Tiêu đề",
        dataIndex: "title",
        key: "title",
        width: 150,
        render: (_, record) => <span>{record.title}</span>,
      },
      {
        title: "Nội dung",
        dataIndex: "description",
        key: "description",
        width: 200,
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <TableContent>{record.description || "--"}</TableContent>
          </div>
        ),
      },
      {
        title: "Loại sự cố",
        dataIndex: "issueCategory",
        key: "issueCategory",
        width: 100,
        render: (_, record) => <span>{record.issueCategory?.name}</span>,
      },
      {
        title: "Người báo cáo",
        dataIndex: "reporterName",
        key: "reporterName",
        width: 140,
        render: (_, record) => <span>{record.reporterName}</span>,
      },
      {
        title: "Số điện thoại",
        dataIndex: "reporterPhone",
        key: "reporterPhone",
        width: 140,
        render: (_, record) => <span>{record.reporterPhone}</span>,
      },

      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (_, record) => {
          const status = record.status as IssueStatus;
          const statusInfo = IssueStatusTrans[status];

          if (!statusInfo) {
            return <Tag>{record.status}</Tag>;
          }

          return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
        },
      },
      {
        title: "Ngày tạo",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 120,
        render: (value) => <span>{formatDate(value, "DD/MM/YYYY HH:mm")}</span>,
      },
      {
        title: "Thao tác",
        key: "action",
        width: 160,
        align: "center",
        fixed: "right",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  setSelectedIssue(record);
                  setVisibleModal(true);
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>

            <Tooltip title="Xóa">
              <Popconfirm
                placement="top"
                title="Xác nhận xóa báo cáo sự cố này?"
                onConfirm={() => handleDelete(record)}
                okText="Xóa"
                cancelText="Không"
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>
          </div>
        ),
      },
    ],
    [query.limit, query.page],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  const fetchCountIssue = async () => {
    const res = await issueApi.count();

    const countIssue = {
      pending: 0,
      processing: 0,
      completed: 0,
      rejected: 0,
    };

    res.data.forEach((item: any) => {
      switch (item.status) {
        case IssueStatus.PENDING:
          countIssue.pending = item.count;
          break;

        case IssueStatus.PROCESSING:
          countIssue.processing = item.count;
          break;

        case IssueStatus.COMPLETED:
          countIssue.completed = item.count;
          break;

        case IssueStatus.REJECTED:
          countIssue.rejected = item.count;
          break;
      }
    });

    setCountIssue(countIssue);
  };

  const fetchIssueCategory = async () => {
    const res = await issueCategoryApi.findAll();
    setIssueCategoryOptions(
      res.data.issueCategories.map((item: IssueCategory) => ({
        label: item.name,
        value: item.id,
      })),
    );
  };

  useEffect(() => {
    fetchCountIssue();
    fetchIssueCategory();
  }, []);

  const [qrValue, setQrValue] = useState("");
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const handleCopyLink = useCallback(async () => {
    if (!qrValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(qrValue);
      message.success("Đã copy link");
    } catch {
      message.error("Copy link thất bại");
    }
  }, [qrValue]);

  const handleCopyQrImage = useCallback(async () => {
    const svg = qrCodeRef.current?.querySelector("svg");
    if (!svg) {
      message.error("Không tìm thấy mã QR");
      return;
    }

    try {
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          URL.revokeObjectURL(url);
          message.error("Copy QR thất bại");
          return;
        }

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        canvas.toBlob(async (blob) => {
          if (!blob) {
            message.error("Copy QR thất bại");
            return;
          }

          try {
            await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
            message.success("Đã copy mã QR");
          } catch {
            message.error("Copy QR thất bại");
          }
        }, "image/png");
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        message.error("Copy QR thất bại");
      };

      img.src = url;
    } catch {
      message.error("Copy QR thất bại");
    }
  }, []);

  useEffect(() => {
    setQrValue("https://ttc.bmdapp.store/report");
  }, []);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            filters={[
              {
                key: "areaId",
                value: issueCategoryId ?? "",
                placeholder: "Loại sự cố",
                width: 200,
                options: issueCategoryOptions || [],
                allowClear: true,
                showSearch: true,
                onChange: (value) => {
                  setIssueCategoryId(value ? Number(value) : undefined);
                  setQuery((prev) => ({
                    ...prev,
                    page: 1,
                    issueCategoryId: value ? Number(value) : null,
                  }));
                },
              },
              {
                type: "dateRange",
                key: "createdAtRange",
                value: dateRange,
                width: 280,
                placeholder: ["Từ ngày", "Đến ngày"],
                onChange: (values) => {
                  setDateRange(values);
                  setQuery((prev) => ({
                    ...prev,
                    page: 1,
                    fromAt: values?.[0] ? values[0].startOf("day").unix() : null,
                    toAt: values?.[1] ? values[1].endOf("day").unix() : null,
                  }));
                },
              },
            ]}
          />
        }
        tabs={
          <div className="flex items-center gap-2 justify-between">
            <Tabs
              activeKey={query.status || "all"}
              onChange={(key) => {
                if (key === "all") {
                  setQuery((prev) => ({ ...prev, status: undefined, page: 1, limit: 20 }));
                } else {
                  setQuery((prev) => ({ ...prev, status: key, page: 1, limit: 20 }));
                }
              }}
              items={[
                {
                  key: "all",
                  label: (
                    <div className="flex items-center gap-2">
                      <span>Tất cả</span>
                    </div>
                  ),
                },
                {
                  key: IssueStatus.PENDING,
                  label: (
                    <div className="flex items-center gap-2">
                      <span>Chờ xử lý</span>
                      <span className="bg-yellow-500 rounded-full text-xs h-5 w-5 flex items-center justify-center text-white">
                        {countIssue.pending}
                      </span>
                    </div>
                  ),
                },
                {
                  key: IssueStatus.PROCESSING,
                  label: (
                    <div className="flex items-center gap-2">
                      <span>Đang xử lý</span>
                      <span className="bg-blue-500 rounded-full text-xs h-5 w-5 flex items-center justify-center text-white">
                        {countIssue.processing}
                      </span>
                    </div>
                  ),
                },
                {
                  key: IssueStatus.COMPLETED,
                  label: (
                    <div className="flex items-center gap-2">
                      <span>Đã xử lý</span>
                      <span className="bg-green-500 rounded-full text-xs h-5 w-5 flex items-center justify-center text-white">
                        {countIssue.completed}
                      </span>
                    </div>
                  ),
                },
                {
                  key: IssueStatus.REJECTED,
                  label: (
                    <div className="flex items-center gap-2">
                      <span>Từ chối</span>
                      <span className="bg-red-500 rounded-full text-xs h-5 w-5 flex items-center justify-center text-white">
                        {countIssue.rejected}
                      </span>
                    </div>
                  ),
                },
              ]}
            />
            <div>
              <div className="flex items-center gap-2">
                <span>Báo cáo sự cố</span>
                <button
                  type="button"
                  onClick={() => setOpenQRModal(true)}
                  aria-label="Xem mã QR"
                  className="text-xl"
                >
                  <QrcodeOutlined style={{ color: "#6c84e5" }} size={20} />
                </button>
              </div>
            </div>
          </div>
        }
        table={
          <AppTable<Issue>
            loading={loading}
            pagination={false}
            rowKey="id"
            dataSource={data}
            columns={columns}
            scroll={{ x: 1100 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <IssueModal
        onSubmitOk={() => {
          fetchData();
          fetchCountIssue();
        }}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        issue={selectedIssue}
      />

      <Modal
        visible={openQRModal}
        closable={false}
        footer={null}
        centered
        width={440}
        onCancel={() => setOpenQRModal(false)}
        styles={{ body: { padding: "40px 48px" } }}
      >
        <div className="flex flex-col items-center">
          <div ref={qrCodeRef} className="mb-6">
            <QRCode value={qrValue} size={220} />
          </div>

          <p className="mb-5 max-w-full break-all text-center text-sm text-gray-400">{qrValue}</p>

          <button
            type="button"
            onClick={handleCopyLink}
            aria-label="Copy link"
            className="mb-10 flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-2.5 text-sm font-medium text-[#6c84e5] transition-colors hover:border-[#6c84e5]/40 hover:bg-gray-50"
          >
            <CopyFilled className="text-base" />
            Copy link
          </button>

          <div className="flex w-full items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleCopyQrImage}
              className="flex flex-1 items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-2.5 text-sm font-medium text-gray-800 transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
              <CopyFilled className="text-base" />
              Copy QR
            </button>
            <button
              type="button"
              onClick={() => setOpenQRModal(false)}
              className="flex flex-1 items-center justify-center rounded-full bg-[#6c84e5] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#5a72d4]"
            >
              Đóng QR
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
});
